import { Action, ActionTracking, Subaction } from "./ActionType";
import { Plan } from "./Plan";
import { WorkoutBuilderConfig } from "./WorkoutBuilder";

// Extend Habit Structure to Include Actions with Subactions
export type Habit = {
  id: string; // Unique identifier for each habit.
  habitName: string; // Name or description of the habit.
  habitDescription: string; // A more detailed description or notes about the habit.
  creationDate: string; // When the habit was first added.
  targetFrequency: string; // How often the user aims to perform the habit (e.g., daily, thrice a week, etc.)

  levels: HabitLevels[];
  learningPlansIds: string[];
  backgroundImageURL: string;
  practiceType?: PracticeType;
  position?: number; // Authored default order in the habit list (lower shows first). The app uses this as the fallback sort when a user has not manually reordered.
  communityId: string;
  authorsIds: string[]; // Which authors encourage this practice
  learningPlans?: Plan[]; // The plans that start this habit
  status?: boolean;
  actions: Action[]; // List of actions associated with the habit.
  standaloneSubactions?: Subaction[]; // UI-only cache; subactions are stored in the Subactions collection.
  tracking?: HabitTracking[]; // List of habit tracking instances.
  // AI generation fields
  useLibrary?: boolean; // Opt-in flag: when true, the syncExerciseLibrary trigger will auto-sync this habit's subactions into an OpenAI vector store.
  vectorStoreId?: string; // OpenAI vector store ID — automatically set by syncExerciseLibrary after the first sync. Pass to generateWorkout.
  vectorStoreItemCount?: number; // Number of items currently indexed in the vector store.
  vectorStoreLastRebuild?: number; // Unix timestamp (ms) of the last vector store rebuild.
  generationPlanPrompt?: string; // Prompt for multi-day plan generation.
  defaultDietType?: string;
  workoutBuilder?: WorkoutBuilderConfig;
  skillId?: string; // Optional Skill (Community/{id}/Skills) whose instruction + references ground this habit's AI guidance. Additive.
};
export enum PracticeType {
  MEDITATION = 1,
  DIET = 2,
  EXERCISE = 3,
  DEFAULT = 4,
  FASTING = 5,
  CUSTOM = 6,
  HYDRATION = 7,
  FOCUS_BLOCK = 8,
  // A habit made of chat-only "prompt" subactions (see ActionType.PROMPT).
  PROMPT_PACK = 9,
  // A check-in habit: the user logs how they feel (1..5) with optional
  // contributing factors and a note. Rendered by the mobile app's mood tracker
  // card/detail panel; authored here with no extra config.
  MOOD_TRACKER = 10,
  // A state-aware library of short regulation practices. Actions are browse
  // categories and subactions are the individual tools.
  REGULATION_TOOLKIT = 11,
}

// Derive a habit's practiceType from its actions' actionType. Used when a habit
// is created without an explicit practiceType (onboarding generation, imports),
// so it does not silently fall back to DEFAULT. "sleep" and "custom" have no
// dedicated PracticeType, so they map to DEFAULT.
export const inferPracticeType = (
  actions: Array<{ actionType?: string }> | undefined | null
): PracticeType => {
  if (!Array.isArray(actions) || actions.length === 0) return PracticeType.DEFAULT;
  const types = actions.map((a) =>
    typeof a?.actionType === "string" ? a.actionType.toLowerCase() : ""
  );
  if (types.some((t) => t === "meditation")) return PracticeType.MEDITATION;
  if (types.some((t) => t === "diet")) return PracticeType.DIET;
  if (types.some((t) => t === "exercise")) return PracticeType.EXERCISE;
  return PracticeType.DEFAULT;
};
export type HabitLevels = {
  levelNumber: number; // An integer representing the level.
  xpRequired: string; // The amount of XP required to reach this level.
  reward: string; // Any reward or feature unlock that happens at this level.
  xpReward: string;
  description: string;
};

export enum HabitStatus {
  COMPLETED = "COMPLETED",
  PARTIALLY_COMPLETED = "PARTIALLY_COMPLETED",
  MISSED = "MISSED",
}

export type HabitTracking = {
  id: string; // Unique identifier for each habit tracking instance.
  date: string; // The date when the user performed the habit.
  status: HabitStatus; // Indicates if the habit was completed, missed, or partially completed on that date.
  notes: string; // Any additional notes the user might want to add for that particular instance.
  actionsTracking?: ActionTracking[]; // List of action tracking instances for this habit tracking instance.
};

export type HabitMilestone = {
  id: string; // Unique identifier for each milestone.
  habitId: string; // Foreign key linking to the Habits Table.
  milestoneName: string; // Name or description of the milestone (e.g., "Meditated for 30 consecutive days").
  milestoneDate: string; // The date when the milestone was achieved.
};

export type HabitCategories = {
  id: string; // Unique identifier for each category.
  categoryName: string; // Name of the habit category (e.g., "Mindfulness", "Physical Exercise", "Study").
};
